import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import withReducer from '@/app/store/withReducer';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { app } from '@/utils/utils';
import { getMessaging, onMessage } from 'firebase/messaging';
import Chat from './Chat';
import ChatsSidebar from './ChatsSidebar';
import * as Actions from './store/actions';
import reducer from './store/reducers';
import './chat.css';
import { ChevronLeft } from '@material-ui/icons';
import { useParams, useLocation } from 'react-router-dom';
import { getConversationsByStudentName } from '@/app/services/messages/messages';

const messaging = getMessaging(app);

const drawerWidth = 300;

const useStyles = makeStyles((theme) => ({
	drawerPaper: {
		width: drawerWidth,
		maxWidth: '100%',
		overflow: 'hidden',
		height: '100%',
		borderRadius: 8,
		[theme.breakpoints.up('md')]: {
			position: 'relative',
		},
	},
	content: {
		display: 'flex',
		flex: '1 1 100%',
		minHeight: 0,
	},
}));

const ChatApp = (props) => {
	const dispatch = useDispatch();
	const chat = useSelector(({ chatApp }) => chatApp.chat.data);
	const contacts = useSelector(({ chatApp }) => chatApp.contacts.entities);
	const selectedContactId = useSelector(({ chatApp }) => chatApp.contacts.selectedContactId);
	const mobileChatsSidebarOpen = useSelector(({ chatApp }) => chatApp.sidebars.mobileChatsSidebarOpen);
	const location = useLocation();
	const [isContactsLoading, setIsContactsLoading] = useState(false);
	const [isChatLoading, setIsChatLoading] = useState(false);
	const { directLinkConversationId } = useParams();
	const { studentName } = location.state || {};

	// Get conversation by student name. Necessary because the one we need might not be in the first page of results
	const openChatByConversationByName = () => {
		getConversationsByStudentName(studentName).then((res) => {
			const updatedContacts = [...contacts, res.data.data[0]];

			dispatch(Actions.updateContacts(updatedContacts));
		});

		dispatch(Actions.setselectedContactId(+directLinkConversationId));
	};

	useEffect(() => {
		if (studentName) {
			openChatByConversationByName();
		}
	}, [studentName]);

	const handleBackgroundChatMessage = (payload) => {
		if (document.visibilityState === 'hidden' && payload.data.click_action === 'chat_push_notification') {
			const { data } = payload.data;
			let { message_data: messageData } = data;
			messageData = JSON.parse(messageData);
			const newContact = {
				created_at: messageData.created_at,
				id: messageData.conversation_id,
				messages_unread_count: data.unread_count,
				meta: messageData.meta,
				student_id: Number(data.student_id),
				student: {
					photo: data.student_photo,
					first_name: data.student_name?.split(' ')[0],
					last_name: data.student_name?.split(' ')[1],
					student_id: Number(data.student_id),
				},
				school: {},
				last_message: {
					attachement: messageData.attachement,
					conversation_id: messageData.conversation_id,
					message: messageData.message,
					message_type: messageData.message_type,
					message_from_role: messageData.message_from_role,
					meta: messageData.meta,
				},
			};
			dispatch(Actions.insertContact(newContact));
			dispatch(Actions.receiveMessage(JSON.parse(data.message_data), data.student_id));
		}
	};

	useEffect(() => {
		navigator.serviceWorker.addEventListener('message', handleBackgroundChatMessage);
		return () => {
			navigator.serviceWorker.removeEventListener('message', handleBackgroundChatMessage);
		};
	}, []);

	onMessage(messaging, (payload) => {
		if (payload?.data?.click_action === 'chat_push_notification') handleChatMessage(payload);
	});

	const handleChatMessage = (payload) => {
		const { data } = payload;
		dispatch(Actions.receiveMessage(JSON.parse(data.message_data), data.student_id));
		let { message_data: messageData } = data;
		messageData = JSON.parse(messageData);
		const newContact = {
			created_at: messageData.created_at,
			id: messageData.conversation_id,
			messages_unread_count: data.unread_count,
			meta: messageData.meta,
			student_id: Number(data.student_id),
			student: {
				photo: data.student_photo,
				first_name: data.student_name?.split(' ')[0],
				last_name: data.student_name?.split(' ')[1],
				student_id: Number(data.student_id),
			},
			school: {},
			last_message: {
				attachement: messageData.attachement,
				conversation_id: messageData.conversation_id,
				message: messageData.message,
				message_type: messageData.message_type,
				message_from_role: messageData.message_from_role,
				meta: messageData.meta,
			},
		};
		if (data.conversation_id == selectedContactId) {
			newContact.messages_unread_count = 0;
		}
		const temp = [newContact];
		temp.push(...contacts.filter((contact) => contact.id != messageData.conversation_id));
		dispatch(Actions.updateContacts(temp));
	};

	const classes = useStyles(props);
	const selectedContact = contacts.find((_contact) => _contact.id === selectedContactId);

	useEffect(() => {
		if (contacts.length === 0) {
			dispatch(Actions.getContacts(setIsContactsLoading));
		}
	}, [dispatch]);

	return (
		<div className="relative p-24 mb-30 max-w-[1400px] w-full mx-auto max-h-[75vh] flex flex-col">
			<div className="flex relative gap-x-20 max-h-[70vh] overflow-hidden min-h-[70vh]">
				<Hidden mdUp>
					<Drawer
						ModalProps={{
							keepMounted: true,
							disablePortal: true,
							BackdropProps: {
								classes: {
									root: 'absolute',
								},
							},
						}}
						anchor="left"
						className="h-full absolute z-20"
						classes={{
							paper: clsx(classes.drawerPaper, 'absolute ltr:left-0 rtl:right-0'),
						}}
						onClose={() => dispatch(Actions.closeMobileChatsSidebar())}
						open={mobileChatsSidebarOpen}
						style={{ position: 'absolute' }}
						variant="temporary"
					>
						<ChatsSidebar
							isContactsLoading={isContactsLoading}
							setIsChatLoading={setIsChatLoading}
							setIsContactsLoading={setIsContactsLoading}
						/>
					</Drawer>
				</Hidden>
				<Hidden smDown>
					<Drawer
						className="h-full z-20"
						classes={{
							paper: classes.drawerPaper,
						}}
						open
						variant="permanent"
					>
						<ChatsSidebar
							isContactsLoading={isContactsLoading}
							setIsChatLoading={setIsChatLoading}
							setIsContactsLoading={setIsContactsLoading}
						/>
					</Drawer>
				</Hidden>
				<main className="flex flex-col flex-1 z-10 w-full rounded-lg overflow-hidden bg-gradient-to-b from-[rgba(255,255,255,0.8)] via-[rgba(255,255,255,0.6)] to-[rgba(255,255,255,0.8)]">
					{!chat && !isChatLoading ? (
						<div className="flex flex-col flex-1 items-center justify-center p-24">
							<Paper className="rounded-full h-96 w-96 flex items-center justify-center">
								<Typography className="text-40" color="secondary">
									chat
								</Typography>
							</Paper>
							<Typography className="my-24" variant="h6">
								Chat App
							</Typography>
							<Typography className="hidden md:flex px-16 pb-24 mt-24 text-center" color="textSecondary">
								Select a chat to start a conversation!..
							</Typography>
							<Button
								className="flex md:hidden normal-case"
								color="primary"
								onClick={() => dispatch(Actions.openMobileChatsSidebar())}
								variant="outlined"
							>
								Select a contact to start a conversation!..
							</Button>
						</div>
					) : (
						<>
							<div className="py-16 bg-white opacity1 flex items-center md:px-16">
								<IconButton
									aria-label="Open drawer"
									className="flex md:hidden p-0 h-30 w-30 ml-4"
									color="inherit"
									onClick={() => dispatch(Actions.openMobileChatsSidebar())}
									size="medium"
								>
									<ChevronLeft className="h-36 w-36 p-4" />
								</IconButton>
								<div className="flex items-center ">
									<div className="relative mx-8 ">
										{selectedContact ? (
											<Avatar alt={selectedContact?.student.first_name} src={selectedContact?.student.thumb}>
												{!selectedContact?.student.thumb || selectedContact?.student.thumb === ''
													? selectedContact?.student.first_name[0]
													: ''}
											</Avatar>
										) : (
											''
										)}
									</div>
									<Typography className="text-14 font-700 px-4" color="inherit">
										{selectedContact
											? `${selectedContact?.student.first_name} ${selectedContact?.student.last_name}`
											: ''}
									</Typography>
								</div>
							</div>
							<hr className="mx-auto opacity-50" style={{ width: '100%', color: 'lightgrey' }} />
							<div className={classes.content}>
								<Chat className="flex flex-1 z-10" isChatLoading={isChatLoading} />
							</div>
						</>
					)}
				</main>
			</div>
		</div>
	);
};

export default withReducer('chatApp', reducer)(ChatApp);
